import React, { useEffect, useState, useRef } from "react";
import { Desktop, Mobile } from "../MediaQuery/MainPageMediaQuery.js";
import styled from "styled-components";
import Banner from "../Components/Banner.jsx";
import BannerMobile from "../Components/BannerMobile.jsx";
import LogBar from "../Components/LogBar.jsx";
import ModalTermsOfService from "../Components/Modal/ModalTermsOfService.jsx";
import ModalInquiry from "../Components/Modal/ModalInquiry.jsx";
import Ask from "../Components/Ask.jsx";
import AskMobile from "../Components/AskMobile.jsx";
// import Loading from "../Components/Loading.jsx";
import HeightInterval from "../Components/HeightInterval.jsx";
import ModalThankLowGrant from "../Components/Modal/ModalThankLowGrant.jsx";
import ModalUnauthorizedUser from "../Components/Modal/ModalUnauthorizedUser.jsx";
import useStore from "../store.js";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Stop from "../Components/Stop";
import { Helmet } from "react-helmet-async";
import SnackbarComponent from "../Components/SnackBar.jsx";
import snackBar_delete from "../img/snackBar_delete.png";
import ModalUserWithdrawal from "../Components/Modal/ModalUserWithdrawal.jsx";
import ModalAppDownload from "../Components/Modal/ModalAppDownload.jsx";
import { trackRecentSignup } from "../utils/signupTracking.js";
import { landingEntryPath } from "../utils/landingExample.js";
import { getLandingExampleQuery } from "../utils/mailAttribution.js";
import { shouldSuppressAppDownloadDuringHandoff } from "../utils/guestChatHandoff.js";

const Main = () => {
  const [showModal, setShowModal] = useState(false); // 회원가입축하 모달창
  const [showUnauthorizedModal, setShowUnauthorizedModal] = useState(false); // Unauthorized 모달창 상태 변수
  const [showAppDownloadModal, setShowAppDownloadModal] = useState(false); // 앱 다운로드 모달창

  const navigate = useNavigate();
  const [isCheckingUser, setIsCheckingUser] = useState(true);
  const initialRender = useRef(true);
  const {
    // loading,
    userLawHistory,
    userInfo,
    updateUserInfo,
    updateUserLaw,
    initializeStore,
    fetchLogs,
    logs,
    showDeleteHistorySnackbar,
    isChatMode,
    fetchReferralCode,
  } = useStore();

  // 앱 다운로드 모달 표시 여부 확인
  useEffect(() => {
    const isInApp = () => {
      const userAgent = navigator.userAgent.toLowerCase();

      // Android WebView 감지: 'wv' 문자열 포함
      if (userAgent.includes('wv')) return true;

      // 커스텀 User-Agent 감지 (앱에서 설정한 경우)
      if (userAgent.includes('lawlow') || userAgent.includes('lawlowapp')) return true;

      // JavaScript로 주입된 앱 플래그 확인 (iOS에서 주로 사용)
      if (window.isLawlowApp === true) return true;

      // iOS WebView 감지 시도 (Safari 브라우저가 아닌 iOS 디바이스)
      const isIOS = /iphone|ipad|ipod/.test(userAgent);
      const isStandalone = window.navigator.standalone === true;
      const isSafari = userAgent.includes('safari');
      const isIOSWebView = isIOS && !isSafari;

      return isStandalone || isIOSWebView;
    };

    const checkShouldShowAppModal = () => {
      // 앱 내에서 접속한 경우 모달을 띄우지 않음
      if (isInApp()) {
        return false;
      }

      // 로그인 후 비로그인 대화를 이전하는 동안에는 다른 모달로 흐름을 가리지 않음
      if (shouldSuppressAppDownloadDuringHandoff(isChatMode)) {
        return false;
      }

      // 로그인 정보가 로드되지 않았으면 미표시
      if (!userInfo) {
        return false;
      }

      // 첫 검색을 마친 유저에게만 모달 표시 (검색 내역이 없으면 미표시)
      if (!logs || logs.length === 0) {
        return false;
      }

      // "다시 보지 않기"를 선택한 경우 미표시 (과거 7일 숨김 값도 영구 숨김으로 처리)
      if (localStorage.getItem('hideAppDownloadModal')) {
        return false;
      }
      return true;
    };

    // 페이지 로드 후 3초 뒤에 모달 표시
    const timer = setTimeout(() => {
      if (checkShouldShowAppModal()) {
        setShowAppDownloadModal(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [isChatMode, userInfo, logs]);

  useEffect(() => {
    fetchLogs(); // 페이지가 로드될 때 서버에 질문내역 데이터 호출
  }, [fetchLogs]);

  // 초대 코드 조회/생성
  useEffect(() => {
    fetchReferralCode(); // 페이지가 로드될 때 초대 코드 조회/생성
  }, [fetchReferralCode]);

  const location = useLocation();
  const textValue = location.state?.textValue ?? getLandingExampleQuery(location.search);

  /* 이용약관 모달창 */
  const [isTermsModalOpen, setTermsModalIsOpen] = useState(false);
  const handleOpenTermsModal = () => {
    setTermsModalIsOpen(true);
  };
  const handleCloseTermsModal = () => {
    setTermsModalIsOpen(false);
  };

  /* 문의하기 모달창 */
  const [isInquiryModalOpen, setInquiryModalIsOpen] = useState(false);
  const handleOpenInquiryModal = () => {
    setInquiryModalIsOpen(true);
  };
  const handleCloseInquiryModal = () => {
    setInquiryModalIsOpen(false);
  };

  const handleConfirmModal = () => {
    setShowModal(false);
    // 팝업을 닫았음을 localStorage에 저장해서
    // 팝업이 한 번 뜬 이후로 다시는 뜨지 않게 동작하게 함
    localStorage.setItem("welcomeModalShown", "true");
  };

  /* 새회원일 경우 회원가입축하 팝업 띄우기 */
  // credit의 내역 개수(length) 및 생성 시간(createdAt) 확인해서 새회원인지 판단
  const checkNewUser = () => {
    if (trackRecentSignup(userLawHistory)) setShowModal(true);
  };

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      let anonymous = false;
      try {
        await updateUserInfo(setShowUnauthorizedModal, () => {
          if (!active) return;
          anonymous = true;
          navigate(landingEntryPath("/", location.search), { replace: true });
        });
        if (active && useStore.getState().userInfo) await updateUserLaw();
        // trackLogin은 App.js에서 전역으로 처리됨
      } catch (error) {
        console.error("Failed to update user info or law history:", error);
      } finally {
        if (active && !anonymous) setIsCheckingUser(false);
      }
    };

    fetchData(); // 비동기 함수 호출
    return () => { active = false; };
  }, []);

  // 사용자의 userLawHistory 데이터가 있을 경우에만 checkNewUser 실행
  useEffect(() => {
    if (userLawHistory && userLawHistory.length > 0) {
      checkNewUser();
    }
  }, [userLawHistory]);

  // 로딩 화면일 때 스크롤 비활성화
  // useEffect(() => {
  //   if (loading) {
  //     // loading 상태일 때 스크롤 비활성화
  //     document.body.style.overflow = "hidden";
  //   } else {
  //     // loading 상태가 아닐 때 스크롤 활성화
  //     document.body.style.overflow = "";
  //   }

  //   return () => {
  //     // 컴포넌트 언마운트 시 스크롤 복구
  //     document.body.style.overflow = "";
  //   };
  // }, [loading]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false; // 첫 렌더링 후에 false로 설정
      return;
    }

    if (!showUnauthorizedModal) {
      // Unauthorized 모달이 닫힐 때 로그인 페이지로 리디렉션 수행
      navigate(landingEntryPath("/", location.search));
    }
  }, [showUnauthorizedModal]);



  // 로컬 스토리지와 Zustand store의 상태를 동기화
  useEffect(() => {
    initializeStore();
  }, []);

  return (
    // <Stop />
    <>
      <Helmet>
        <title>나의 상황과 비슷한 판례 찾기 | 로우로우</title>
        <link rel="canonical" href="https://www.lawlow.info/main" />
        <meta
          name="description"
          content="내 상황을 법원은 어떻게 생각할까? 로우로우에서 나의 문제상황을 입력하세요. 비슷한 판례를 찾아드립니다!"
        />
        <meta
          name="keywords"
          content="변호사, 변호사 추천, 선임비용, 무료법률상담, 전화법률상담, 법률상담, 법, 법률, 사기, 이혼, 범죄, 채무"
        />
      </Helmet>

      <ModalThankLowGrant
        isOpen={showModal}
        onConfirm={handleConfirmModal}
        messageTitle="회원가입을 진심으로 축하드립니다!"
        messageSubtitle="축하 기념 10로우를 지급했습니다."
      />
      {showUnauthorizedModal && (
        <ModalUnauthorizedUser
          isOpen={showUnauthorizedModal}
          onClose={() => setShowUnauthorizedModal(false)}
        />
      )}
      <div inert={isCheckingUser ? "" : undefined} style={{ display: "contents" }}>
      <Desktop>
        {/* {loading && <Loading />} */}        
        {/* <Wrapper isLoading={loading}> */}
        <Wrapper>
          <LogBar />
          <MainWrapper>
            <Banner />
            <Ask initialTextValue={textValue} />
          </MainWrapper>
        </Wrapper>
      </Desktop>

      <Mobile>
        {/* {loading && <Loading />} */}
        {/* 삭제 & 복사 스낵바 컴포넌트는 전역 상태에 따라 표시 */}
        <SnackbarComponent
          visible={showDeleteHistorySnackbar}
          src={snackBar_delete}
          top={72}
        />
        {/* <WrapperMobile isLoading={loading}> */}
        <WrapperMobile $isChatMode={isChatMode}>
          {!isChatMode && (
            <>
              <HeightInterval height={24} />
              <BannerMobile />
              <HeightInterval height={46} />
            </>
          )}
          <AskMobile initialTextValue={textValue} />
          {/* (24-09-04)PC, Mobile 모두 버튼이 사이드바로 이동되기로 디자인 변경되어 주석처리함
          <FooterDiv>
            <HeightInterval height={16} />
            <FooterText>
              <ModalInquiry
                isOpen={isInquiryModalOpen}
                onCloseInquiry={handleCloseInquiryModal}
              />
              <span onClick={() => handleOpenInquiryModal(true)}>문의하기</span>
              <Text> | </Text>
              <ModalTermsOfService
                isOpen={isTermsModalOpen}
                onClose={handleCloseTermsModal}
              />
              <span onClick={() => handleOpenTermsModal(true)}>이용약관</span>
            </FooterText>
            <HeightInterval height={22} />
          </FooterDiv> */}
        </WrapperMobile>
      </Mobile>
      </div>
      <ModalUserWithdrawal isOpen={false} onClose={() => {}} />
      
      {/* 앱 다운로드 모달 */}
      <ModalAppDownload 
        isOpen={showAppDownloadModal}
        onClose={() => setShowAppDownloadModal(false)}
      />
    </>
  );
};

const Wrapper = styled.div`
  display: block;
`;

const WrapperMobile = styled.div`
  display: block;
  width: auto;
  margin: ${({ $isChatMode }) => ($isChatMode ? '0' : '0px 24px')};
  min-height: ${({ $isChatMode }) => ($isChatMode ? '100dvh' : 'auto')};
  overflow: ${({ $isChatMode }) => ($isChatMode ? 'hidden' : 'visible')};

  @media (max-width: 1140px) {
    margin: ${({ $isChatMode }) => ($isChatMode ? '0' : '0px 36px')};
  }

  /* iOS, Android 모바일 디바이스 기종들을 총 고려해봤을 때, 너비 410px 이하일 때 너비 24px로 조정 */
  @media (max-width: 410px) {
    margin: ${({ $isChatMode }) => ($isChatMode ? '0' : '0px 24px')};
  }
`;

const MainWrapper = styled.div`
  width: calc(100% - 312px); // LogBar의 너비 312px 이외 영역
  margin-left: 312px; /* LogBar의 영역 logBarMinWidth 20% */
`;

// (24-09-04)PC, Mobile 모두 버튼이 사이드바로 이동되기로 디자인 변경되어 주석처리함
// const FooterText = styled.div`
//   color: rgba(0, 0, 0, 0.3);
//   font-family: Pretendard;
//   font-size: 14px;
//   font-style: normal;
//   font-weight: 400;
//   line-height: 26px;
// `;

// const FooterDiv = styled.div`
//   display: flex;
//   justify-content: center;
//   font-size: 12px;

//   @media (max-width: 599px) {
//     display: none;
//   }
// `;

export default Main;

